// src/pages/compliance/HomeTab.jsx
//
// Extracted from CompliancePage.jsx's inline HomeTab -- the landing view
// for the Compliance module: hero banner, the "choose a tracker" card grid,
// how-it-works steps, and key features. Pure presentation; CompliancePage
// still owns all navigation state, passed in via props.
import PropTypes from 'prop-types'
import {
  FileText, ShieldAlert, TrendingUp, Scale, ListChecks, Link2,
  Upload, Cpu, CheckCircle2, Download, Zap, Sparkles, ShieldCheck, ArrowRight,
} from 'lucide-react'
import { TAB } from '../../utils/compliance/constants'

const HOW_IT_WORKS = [
  {
    Icon: Upload,
    heading: 'Upload your SOW',
    detail: 'Drop your Statement of Work (.pdf or .docx) into the upload area of the feature you want to run.',
  },
  {
    Icon: Cpu,
    heading: 'AI Analysis',
    detail: 'The pipeline parses, chunks, and embeds your document, then runs Azure OpenAI inference to extract all relevant data.',
  },
  {
    Icon: CheckCircle2,
    heading: 'Monitor Progress',
    detail: 'Watch the pipeline run step-by-step with live logs and a progress indicator so you always know what\'s happening.',
  },
  {
    Icon: Download,
    heading: 'Download Artifact',
    detail: 'Receive a fully populated SMP or RAID tracker — ready to share, no editing needed.',
  },
]

const KEY_FEATURES = [
  {
    Icon: Zap,
    title: 'Fully automated',
    desc: 'End-to-end pipelines with up to 8 steps run automatically — no manual copy-paste from your SOW.',
  },
  {
    Icon: Sparkles,
    title: 'Azure OpenAI powered',
    desc: 'Leverages Azure OpenAI with structured prompts and retrieval-augmented generation for high-accuracy extraction.',
  },
  {
    Icon: ShieldCheck,
    title: 'Multiple output formats',
    desc: 'SMP Generation outputs .docx; RAID Tracker outputs .xlsx — both ready for immediate distribution.',
  },
]

export default function HomeTab({ onNavigate, stmAllowed, rtmAllowed }) {
  return (
    <>
      {/* Hero card */}
      <div className="relative overflow-hidden bg-white border border-line rounded-card p-8">
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-brand-700 bg-brand-50 px-2.5 py-1 rounded mb-3.5">
            <Cpu size={11} />
            Compliance Trackers
          </div>
          <h1 className="idp-hero-title">
            Transform SOWs into <em className="not-italic text-brand-600">structured trackers</em> in minutes
          </h1>
          <p className="idp-hero-sub max-w-2xl">
            Upload a Statement of Work and generate your SMP, RAID Tracker, Track Value
            Repository, Obligations Tracker, or Service Traceability Matrix — extracting,
            analyzing, and populating every field using Azure OpenAI.
          </p>
        </div>
      </div>

      <div>
        <h2 className="idp-section-label">Choose a tracker</h2>
        <div className="grid grid-cols-4 gap-3.5">

          <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
            onClick={() => onNavigate(TAB.SMP)}>
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
              <FileText size={20} />
            </div>
            <p className="text-sm font-semibold text-ink m-0">SMP Generation</p>
            <p className="text-xs text-muted leading-relaxed m-0 flex-1">Auto-generates a fully populated Service Management Plan from your SOW with field extraction and confidence scoring.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
               onClick={e => { e.stopPropagation(); onNavigate(TAB.SMP) }}>
              <ArrowRight size={12} /> Generate SMP
            </button>
          </div>

          <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
            onClick={() => onNavigate(TAB.RAID)}>
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
              <ShieldAlert size={20} />
            </div>
            <p className="text-sm font-semibold text-ink m-0">RAID Tracker</p>
            <p className="text-xs text-muted leading-relaxed m-0 flex-1">Identifies risks, assumptions, issues and dependencies from your SOW and populates a structured RAID tracker Excel file.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
              onClick={e => { e.stopPropagation(); onNavigate(TAB.RAID) }}>
              <ArrowRight size={12} /> Generate RAID
            </button>
          </div>

          <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
            onClick={() => onNavigate(TAB.TVR)}>
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
              <TrendingUp size={20} />
            </div>
            <p className="text-sm font-semibold text-ink m-0 flex items-center gap-2">
              Track Value
              <span className="text-[9px] font-bold tracking-wide bg-brand-50 text-brand-700 px-1.5 py-0.5 rounded">NEW</span>
            </p>
            <p className="text-xs text-muted leading-relaxed m-0 flex-1">Surfaces proactive value opportunities beyond the contracted SOW scope — helping teams demonstrate extra delivery impact.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
              onClick={e => { e.stopPropagation(); onNavigate(TAB.TVR) }}>
              <ArrowRight size={12} /> Generate TVR
            </button>
          </div>

          <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
            onClick={() => onNavigate(TAB.OBLIGATION)}>
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
              <Scale size={20} />
            </div>
            <p className="text-sm font-semibold text-ink m-0 flex items-center gap-2">
              Obligations
              <span className="text-[9px] font-bold tracking-wide bg-brand-50 text-brand-700 px-1.5 py-0.5 rounded">NEW</span>
            </p>
            <p className="text-xs text-muted leading-relaxed m-0 flex-1">Extracts every contractual, legal, regulatory and security obligation from your SOW — each with its clause reference, owner, impact and severity.</p>
            <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
              onClick={e => { e.stopPropagation(); onNavigate(TAB.OBLIGATION) }}>
              <ArrowRight size={12} /> Generate Tracker
            </button>
          </div>

          {stmAllowed && (
            <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
              onClick={() => onNavigate(TAB.STM)}>
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
                <ListChecks size={20} />
              </div>
              <p className="text-sm font-semibold text-ink m-0">Service Traceability Matrix</p>
              <p className="text-xs text-muted leading-relaxed m-0 flex-1">Extracts every service, SLA, support window, and staffing entry from your SOW and populates a structured Service Traceability Matrix Excel file.</p>
              <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
                onClick={e => { e.stopPropagation(); onNavigate(TAB.STM) }}>
                <ArrowRight size={12} /> Generate STM
              </button>
            </div>
          )}

          {rtmAllowed && (
            <div className="bg-white border border-line rounded-card p-5 flex flex-col gap-2.5 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-500 hover:shadow-card"
              onClick={() => onNavigate(TAB.RTM)}>
              <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
                <Link2 size={20} />
              </div>
              <p className="text-sm font-semibold text-ink m-0">Requirements Traceability Matrix</p>
              <p className="text-xs text-muted leading-relaxed m-0 flex-1">Extracts every requirement from your requirements document and traces each one back to its exact section into a structured Requirements Traceability Matrix Excel file.</p>
              <button className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-600 hover:text-white transition-all self-start mt-0.5 border-0 cursor-pointer font-[inherit]"
                onClick={e => { e.stopPropagation(); onNavigate(TAB.RTM) }}>
                <ArrowRight size={12} /> Generate RTM
              </button>
            </div>
          )}

        </div>
      </div>

      {/* How it works */}
      <div className="bg-white border border-line rounded-card p-6">
        <h2 className="idp-section-label">How it works</h2>
        <div className="grid grid-cols-4 gap-0 relative">
          <div className="absolute top-4.25 left-[calc(12.5%+17px)] right-[calc(12.5%+17px)] h-px bg-line" />
          {HOW_IT_WORKS.map((step) => (
            <div key={step.heading} className="flex flex-col items-center text-center gap-2.5 px-2.5">
              <div className="w-9 h-9 rounded-full bg-brand-50 border-2 border-line flex items-center justify-center text-brand-600 relative z-10 shrink-0">
                <step.Icon size={17} />
              </div>
              <p className="text-xs font-semibold text-ink m-0 leading-tight">{step.heading}</p>
              <p className="text-[11.5px] text-muted leading-relaxed m-0">{step.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="idp-section-label">Key features</h2>
        <div className="grid grid-cols-3 gap-3.5">
          {KEY_FEATURES.map((feat) => (
            <div key={feat.title} className="bg-white border border-line rounded-card p-5">
              <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600 mb-3">
                <feat.Icon size={16} />
              </div>
              <p className="text-sm font-semibold text-ink m-0 mb-1">{feat.title}</p>
              <p className="text-xs text-muted leading-relaxed m-0">{feat.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

HomeTab.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  stmAllowed: PropTypes.bool.isRequired,
  rtmAllowed: PropTypes.bool.isRequired,
}
